import { useTranslation } from "react-i18next";
import { formatCurrency, formatPercent } from "@/utils/formatters";
import { WrappedCardFrame } from "./WrappedCardFrame";
import type { MonthSummaryData, WrappedCard } from "../types";

interface MonthSummaryCardProps {
  card: WrappedCard;
}

export const MonthSummaryCard = ({ card }: MonthSummaryCardProps) => {
  const { t } = useTranslation("wrapped");
  const data = card.data as unknown as MonthSummaryData;

  const comparisonKey =
    data.delta_pct === null
      ? null
      : data.delta_pct > 0
        ? "wrapped.spentMore"
        : data.delta_pct < 0
          ? "wrapped.spentLess"
          : "wrapped.spentSame";

  return (
    <WrappedCardFrame title={card.title} body={card.body}>
      {data.expense_income_pct != null ? (
        <p className="mb-4 rounded-[var(--ds-radius-md)] bg-[var(--app-primary-chip-bg)] px-4 py-3 text-sm font-semibold text-[var(--app-text)]">
          {t("wrapped.expenseIncomeRatio", { amount: formatCurrency(data.expense_income_pct / 100) })}
        </p>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-3">
        <Metric label={t("wrapped.income")} value={formatCurrency(data.income)} tone="success" />
        <Metric label={t("wrapped.expenses")} value={formatCurrency(data.expenses)} tone="danger" />
        <Metric
          label={t("wrapped.balance")}
          value={formatCurrency(data.balance)}
          tone={data.balance < 0 ? "danger" : data.balance > 0 ? "success" : "primary"}
        />
      </div>
      {comparisonKey ? (
        <p className="mt-4 text-sm font-semibold text-[var(--app-text-secondary)]">
          {t(comparisonKey, { percent: formatPercent(Math.abs(data.delta_pct ?? 0)) })}
        </p>
      ) : null}
    </WrappedCardFrame>
  );
};

const Metric = ({ label, value, tone }: { label: string; value: string; tone: "success" | "danger" | "primary" }) => {
  const colorByTone = {
    success: "text-[var(--app-success-text)]",
    danger: "text-[var(--app-danger-text)]",
    primary: "text-[var(--app-primary)]",
  } as const;

  return (
    <div className="app-panel-raised-inset min-w-0 p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--app-muted-text)]">{label}</p>
      <p className={`mt-2 break-words font-[var(--app-font-display)] text-xl font-semibold tabular-nums ${colorByTone[tone]}`}>{value}</p>
    </div>
  );
};
